// cloudgrid brain refresh <slug> — Grid Brain Phase 2 (PR4).
//
// Asks the platform to re-run the brain hooks (re-classify description / tags /
// diagram) for an entity. The server emits the `entity.brain.manual_refresh`
// trigger and returns immediately — the hooks are async (decision 031 §1.2), so
// the default is fire-and-forget: print the request acknowledgement and point
// the user at `cloudgrid status <slug>`.
//
// --wait turns it blocking client-side: after the POST, poll the entity's
// brain-runs log (GET /api/v2/entities/:id/brain-runs) every 3s up to 60s for a
// run carrying the emitted event_id that has reached a terminal status, then
// print the per-hook outcome. If no run completes in the window — e.g. no hook
// is registered against the trigger yet — print a timeout note (not an error)
// and point at `status`.
//
// --org rebinds the client to the resolved org for the call (backlog 320):
// without it, the request would carry the persisted active-org slug as
// `x-cloudgrid-org` and the server would resolve the entity in the wrong org.

import { Command } from 'commander';
import inquirer from 'inquirer';
import { getV2Client, type IV2Client } from '../api/v2-client.js';
import { getActiveOrg } from '../active-org.js';
import { Output } from '../output.js';
import { handleV2Error } from '../handle-error.js';
import { CloudGridError } from '../utils.js';

// ── API response shapes ─────────────────────────────────────────────────────

// POST /api/v2/entities/:id/brain-refresh → 202
export interface BrainRefreshResponse {
  event_id: string;
  message: string;
}

// One row from GET /api/v2/entities/:id/brain-runs (mirrors routes/v2/brain-runs.ts).
export interface BrainRunItem {
  hook_id: string;
  hook_version: number;
  trigger: string;
  event_id: string;
  status: 'ok' | 'skipped' | 'failed' | 'running';
  tokens_used: number;
  started_at: string;
  finished_at: string | null;
  reason: string | null;
  output_summary: string | null;
}

export interface BrainRunsResponse {
  items: BrainRunItem[];
  next_cursor: string | null;
}

// ── Options ─────────────────────────────────────────────────────────────────

export interface BrainRefreshOpts {
  json?: boolean;
  wait?: boolean;
  org?: string;
}

// Injectable for tests; production defaults poll every 3s for up to 60s.
export interface BrainRefreshDeps {
  client?: IV2Client;
  sleep?: (ms: number) => Promise<void>;
  pollIntervalMs?: number;
  maxWaitMs?: number;
}

const DEFAULT_POLL_INTERVAL_MS = 3_000;
const DEFAULT_MAX_WAIT_MS = 60_000;
const TERMINAL_STATUSES = new Set(['ok', 'skipped', 'failed']);

function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ── Rendering (§23 voice: plain, specific, no marketing, no emoji) ──────────

function renderRunOutcome(slug: string, runs: BrainRunItem[]): string {
  const lines: string[] = [`${slug} · brain refresh complete`];
  for (const r of runs) {
    const summary = r.output_summary ?? (r.status === 'failed' ? r.reason ?? 'failed' : '—');
    lines.push(`  ${r.hook_id.padEnd(22)} ${r.status.padEnd(8)} ${summary}`);
  }
  lines.push('');
  lines.push(`Run 'cloudgrid status ${slug}' to see the updated entity.`);
  return lines.join('\n');
}

// ── Core runner ─────────────────────────────────────────────────────────────

/**
 * Core implementation. Accepts injectable deps (client + clock) for tests; in
 * production the client is created via `getV2Client(opts.org)` and the clock is
 * real.
 */
export async function runBrainRefresh(
  slug: string,
  opts: BrainRefreshOpts,
  deps: BrainRefreshDeps = {},
): Promise<void> {
  const out = Output.fromOpts({ json: !!opts.json, quiet: false });

  const baseClient = deps.client ?? (await getV2Client(opts.org));
  // Server-authoritative org resolution; `--org` overrides the persisted active
  // org for this call. Throws CloudGridError on every failure mode.
  const activeOrg = await getActiveOrg(baseClient, { override: opts.org });
  // Backlog 320 — rebind so the refresh carries the right `x-cloudgrid-org`,
  // not the persisted active-org slug.
  const apiClient = baseClient.withOrgSlug(activeOrg.slug);

  const refresh = await apiClient.post<BrainRefreshResponse>(
    `/api/v2/entities/${encodeURIComponent(slug)}/brain-refresh`,
  );

  if (!opts.wait) {
    out.json(() => refresh);
    out.human(
      () =>
        `${refresh.message} Run 'cloudgrid status ${slug}' to see results.`,
    );
    return;
  }

  // --wait: poll the brain-runs log for a run carrying this event_id that has
  // reached a terminal status.
  const sleep = deps.sleep ?? defaultSleep;
  const pollIntervalMs = deps.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  const maxWaitMs = deps.maxWaitMs ?? DEFAULT_MAX_WAIT_MS;
  const deadline = Date.now() + maxWaitMs;

  let terminal: BrainRunItem[] = [];
  while (Date.now() < deadline) {
    await sleep(pollIntervalMs);
    const runs = await apiClient.get<BrainRunsResponse>(
      `/api/v2/entities/${encodeURIComponent(slug)}/brain-runs`,
      { limit: 50 },
    );
    const mine = (runs.items ?? []).filter((r) => r.event_id === refresh.event_id);
    // Wait until every run for this event is terminal (a hook may report
    // `running` before `ok`/`failed`); report once none are still running.
    if (mine.length > 0 && mine.every((r) => TERMINAL_STATUSES.has(r.status))) {
      terminal = mine;
      break;
    }
  }

  if (terminal.length > 0) {
    out.json(() => ({ event_id: refresh.event_id, runs: terminal }));
    out.human(() => renderRunOutcome(slug, terminal));
    return;
  }

  // Timed out — not an error. The trigger is on the bus; a hook may complete
  // later, or none may be registered against it yet.
  out.json(() => ({ event_id: refresh.event_id, runs: [], timed_out: true }));
  out.human(
    () =>
      `Brain refresh requested, but no run completed within ${Math.round(
        maxWaitMs / 1000,
      )}s. Run 'cloudgrid status ${slug}' to check later.`,
  );
}

// ════════════════════════════════════════════════════════════════════════════
// cloudgrid brain banned-list show | add | remove — classify Phase 4 (spec §4.6)
// ════════════════════════════════════════════════════════════════════════════
//
// Manages the dynamic banned-tag list (spec #740 §4) without hand-editing
// Firestore. Two scopes:
//   - org (default for writes)   — the active org's `{ add, remove }` override.
//   - system                     — the platform default, system-org admin only.
//
// `show` defaults to the effective merge `(system ∪ org.add) \ org.remove`.
// `--org` rebinds the request to another org (cross-org admin work), exactly as
// `brain refresh` does. `add`/`remove` confirm before submit unless `--yes`.

// ── API response shape (mirrors routes/v2/brain-banned-tags.ts) ─────────────

interface BannedTagsView {
  org_id: string;
  system: string[];
  org_add: string[];
  org_remove: string[];
  effective: string[];
}

type ShowScope = 'system' | 'org' | 'effective';
type WriteScope = 'system' | 'org';

interface BannedTagsWriteResponse extends BannedTagsView {
  scope: WriteScope;
  word: string;
  action: 'banned' | 're-permitted';
  was_system_default: boolean;
}

interface BannedTagsShowResponse extends BannedTagsView {
  scope: ShowScope;
}

// ── Options + deps ──────────────────────────────────────────────────────────

export interface BannedListShowOpts {
  json?: boolean;
  scope?: string;
  org?: string;
}

export interface BannedListWriteOpts {
  json?: boolean;
  scope?: string;
  org?: string;
  reason?: string;
  yes?: boolean;
}

// Injectable for tests: the client and the confirmation prompt.
export interface BannedListDeps {
  client?: IV2Client;
  confirm?: (message: string) => Promise<boolean>;
}

async function defaultConfirm(message: string): Promise<boolean> {
  const { ok } = await inquirer.prompt([
    { type: 'confirm', name: 'ok', message, default: false },
  ]);
  return ok;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

const BANNED_TAGS_PATH = '/api/v2/admin/brain/banned-tags';

/**
 * Slugify a word the same way the server does (lowercase, hyphenate, trim
 * dashes), so the confirmation prompt, the success message, and the stored
 * form always agree.
 */
function slugifyWord(word: string): string {
  return word
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function parseShowScope(raw: string | undefined): ShowScope {
  const v = raw ?? 'effective';
  if (v === 'system' || v === 'org' || v === 'effective') return v;
  throw new CloudGridError("--scope must be one of: system, org, effective.");
}

function parseWriteScope(raw: string | undefined): WriteScope {
  const v = raw ?? 'org';
  if (v === 'system' || v === 'org') return v;
  throw new CloudGridError('--scope must be one of: system, org.');
}

/** Greedy word-wrap a comma-separated list, 2-space indent, ~76-col width. */
function renderWrappedList(words: string[]): string {
  if (words.length === 0) return '  (none)';
  const indent = '  ';
  const width = 76;
  const lines: string[] = [];
  let line = indent;
  words.forEach((word, i) => {
    const piece = i < words.length - 1 ? `${word}, ` : word;
    if (line !== indent && (line + piece).length > width) {
      lines.push(line.replace(/\s+$/, ''));
      line = indent;
    }
    line += piece;
  });
  if (line.trim() !== '') lines.push(line.replace(/\s+$/, ''));
  return lines.join('\n');
}

function renderShow(orgSlug: string, scope: ShowScope, view: BannedTagsView): string {
  const s = view.system.length;
  const a = view.org_add.length;
  const r = view.org_remove.length;

  if (scope === 'system') {
    return [`Platform default banned tags (${s}):`, renderWrappedList(view.system)].join('\n');
  }

  if (scope === 'org') {
    if (a === 0 && r === 0) {
      return [
        `Org overrides for ${orgSlug}:`,
        `  No overrides. ${orgSlug} inherits the platform default (${s} tags).`,
      ].join('\n');
    }
    const lines = [`Org overrides for ${orgSlug}:`];
    if (a > 0) lines.push(`  Added (${a}): ${view.org_add.join(', ')}`);
    if (r > 0) lines.push(`  Re-permitted (${r}): ${view.org_remove.join(', ')}`);
    return lines.join('\n');
  }

  // effective
  return [
    `Effective banned tags for ${orgSlug} (${s} system + ${a} org add, ${r} org remove):`,
    renderWrappedList(view.effective),
  ].join('\n');
}

/** The one-line breakdown printed after a write. */
function renderEffectiveSummary(orgSlug: string, view: BannedTagsView): string {
  const s = view.system.length;
  const a = view.org_add.length;
  const r = view.org_remove.length;
  return `Effective banned tags for ${orgSlug}: ${view.effective.length} (${s} system + ${a} org add, ${r} org remove).`;
}

// ── show ────────────────────────────────────────────────────────────────────

export async function runBannedListShow(
  opts: BannedListShowOpts,
  deps: BannedListDeps = {},
): Promise<void> {
  const out = Output.fromOpts({ json: !!opts.json, quiet: false });
  const scope = parseShowScope(opts.scope);

  const baseClient = deps.client ?? (await getV2Client(opts.org));
  const activeOrg = await getActiveOrg(baseClient, { override: opts.org });
  const apiClient = baseClient.withOrgSlug(activeOrg.slug);

  const view = await apiClient.get<BannedTagsShowResponse>(BANNED_TAGS_PATH, { scope });

  out.json(() => view);
  out.human(() => renderShow(activeOrg.slug, scope, view));
}

// ── add / remove ────────────────────────────────────────────────────────────

/**
 * Shared core for `add` (ban) and `remove` (re-permit). `action` selects the
 * HTTP verb and the wording; everything else — org resolution, the confirm
 * gate, the post-write summary — is identical.
 */
async function runBannedListWrite(
  action: 'ban' | 'unban',
  rawWord: string,
  opts: BannedListWriteOpts,
  deps: BannedListDeps,
): Promise<void> {
  const out = Output.fromOpts({ json: !!opts.json, quiet: false });
  const scope = parseWriteScope(opts.scope);
  const word = slugifyWord(rawWord);
  if (word === '') {
    throw new CloudGridError('Provide a word to ban or re-permit, e.g. `cloudgrid brain banned-list add spreadsheet`.');
  }

  const baseClient = deps.client ?? (await getV2Client(opts.org));
  const activeOrg = await getActiveOrg(baseClient, { override: opts.org });
  const apiClient = baseClient.withOrgSlug(activeOrg.slug);

  // Confirm before submit (unless --yes). The prompt names the scope so a
  // system-wide edit can't be mistaken for an org-only one.
  if (!opts.yes && out.isHuman) {
    const target = scope === 'system' ? 'every grid (the platform default)' : activeOrg.slug;
    const verb = action === 'ban' ? 'Ban' : 'Re-permit';
    const confirm = deps.confirm ?? defaultConfirm;
    const ok = await confirm(`${verb} "${word}" as a tag for ${target}?`);
    if (!ok) {
      out.human(() => 'Cancelled.');
      return;
    }
  }

  let res: BannedTagsWriteResponse;
  if (action === 'ban') {
    const body: Record<string, unknown> = { word, scope };
    if (opts.reason) body.reason = opts.reason;
    res = await apiClient.post<BannedTagsWriteResponse>(BANNED_TAGS_PATH, body);
  } else {
    const path = `${BANNED_TAGS_PATH}/${encodeURIComponent(word)}${scope === 'system' ? '?scope=system' : ''}`;
    res = await apiClient.del<BannedTagsWriteResponse>(path);
  }

  out.json(() => res);
  out.human(() => `${renderWriteOutcome(action, scope, word, activeOrg.slug, res)}\n${renderEffectiveSummary(activeOrg.slug, res)}`);
}

function renderWriteOutcome(
  action: 'ban' | 'unban',
  scope: WriteScope,
  word: string,
  orgSlug: string,
  res: BannedTagsWriteResponse,
): string {
  if (action === 'ban') {
    if (scope === 'system') {
      return `Added "${word}" to the platform default banned tags. Takes effect on the next classification.`;
    }
    return `Added "${word}" to ${orgSlug}'s banned tags. Takes effect on the next classification.`;
  }
  // unban
  if (scope === 'system') {
    return `Removed "${word}" from the platform default banned tags. Takes effect on the next classification.`;
  }
  if (res.was_system_default) {
    return `"${word}" is a platform default. Re-permitted it for ${orgSlug}; it can be used as a tag again. Takes effect on the next classification.`;
  }
  return `Re-permitted "${word}" for ${orgSlug}; it can be used as a tag again. Takes effect on the next classification.`;
}

export async function runBannedListAdd(
  word: string,
  opts: BannedListWriteOpts,
  deps: BannedListDeps = {},
): Promise<void> {
  await runBannedListWrite('ban', word, opts, deps);
}

export async function runBannedListRemove(
  word: string,
  opts: BannedListWriteOpts,
  deps: BannedListDeps = {},
): Promise<void> {
  await runBannedListWrite('unban', word, opts, deps);
}

// ── Commander wiring ──────────────────────────────────────────────────────

/**
 * Build the `brain` command tree. A factory (not a singleton) so it can mount
 * under both the top-level program (deprecated, BL598 Phase 1A) and the
 * `admin` parent (canonical) without a commander parent collision — every
 * subcommand is constructed fresh per call.
 */
export function makeBrainCommand(): Command {
const refreshCommand = new Command('refresh')
  .description('Re-run the brain hooks for an entity (re-classify description, tags, diagram)')
  .argument('<slug>', 'Entity name or UUID')
  .option('--json', 'Machine-readable JSON output')
  .option('--wait', 'Wait for the refresh to complete (polls up to 60s)')
  .option('--org <slug>', "Target another grid's entity")
  .action(async (slug: string, opts: BrainRefreshOpts) => {
    try {
      await runBrainRefresh(slug, opts);
    } catch (err) {
      await handleV2Error(err, { json: !!opts.json });
    }
  });

// ── banned-list subcommands (classify Phase 4, spec §4.6) ───────────────────

const bannedListShowCommand = new Command('show')
  .description("Show the banned-tag list for your grid (default: the effective merge)")
  .option('--scope <scope>', 'system | org | effective', 'effective')
  .option('--org <slug>', "Target another grid")
  .option('--json', 'Machine-readable JSON output')
  .action(async (opts: BannedListShowOpts) => {
    try {
      await runBannedListShow(opts);
    } catch (err) {
      await handleV2Error(err, { json: !!opts.json });
    }
  });

const bannedListAddCommand = new Command('add')
  .description('Ban a word as a tag (default scope: your grid)')
  .argument('<word>', 'The word to ban')
  .option('--scope <scope>', 'system | org', 'org')
  .option('--org <slug>', "Target another grid")
  .option('--reason <text>', 'Why this word is banned (recorded for audit)')
  .option('--yes', 'Skip the confirmation prompt')
  .option('--json', 'Machine-readable JSON output')
  .action(async (word: string, opts: BannedListWriteOpts) => {
    try {
      await runBannedListAdd(word, opts);
    } catch (err) {
      await handleV2Error(err, { json: !!opts.json });
    }
  });

const bannedListRemoveCommand = new Command('remove')
  .description('Re-permit a word as a tag (default scope: your grid)')
  .argument('<word>', 'The word to re-permit')
  .option('--scope <scope>', 'system | org', 'org')
  .option('--org <slug>', "Target another grid")
  .option('--yes', 'Skip the confirmation prompt')
  .option('--json', 'Machine-readable JSON output')
  .action(async (word: string, opts: BannedListWriteOpts) => {
    try {
      await runBannedListRemove(word, opts);
    } catch (err) {
      await handleV2Error(err, { json: !!opts.json });
    }
  });

const bannedListCommand = new Command('banned-list')
  .description('Manage the Grid Brain banned-tag list for your grid')
  .addCommand(bannedListShowCommand)
  .addCommand(bannedListAddCommand)
  .addCommand(bannedListRemoveCommand);

  return new Command('brain')
    .description("Refresh an entity's Grid Brain metadata and manage its banned-tag list")
    .addCommand(refreshCommand)
    .addCommand(bannedListCommand);
}

export const brainCommand = makeBrainCommand();
